import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from './auth.service';

export enum EditModeState {
  Viewing = 'viewing',
  EntryConfirming = 'entry-confirming',
  Editing = 'editing',
  ExitConfirming = 'exit-confirming',
  SaveConfirming = 'save-confirming',
  DiscardConfirming = 'discard-confirming',
  Persisting = 'persisting',
  Reverting = 'reverting'
}

const EDIT_PULSE_STORAGE_PREFIX = 'report_editor_pulse_seen_';

@Injectable({ providedIn: 'root' })
export class EditModeStateService {
  private readonly state$ = new BehaviorSubject<EditModeState>(EditModeState.Viewing);

  constructor(private authService: AuthService) {}

  get currentState(): EditModeState {
    return this.state$.value;
  }

  get state(): Observable<EditModeState> {
    return this.state$.asObservable();
  }

  get isEditing(): Observable<boolean> {
    return this.state$.pipe(
      map(s => s === EditModeState.Editing),
      distinctUntilChanged()
    );
  }

  get isEditWorkflowActive(): Observable<boolean> {
    return this.state$.pipe(
      map(s => s !== EditModeState.Viewing && s !== EditModeState.EntryConfirming),
      distinctUntilChanged()
    );
  }

  get isFiltersLocked(): Observable<boolean> {
    return this.state$.pipe(
      map(s => s !== EditModeState.Viewing),
      distinctUntilChanged()
    );
  }

  requestEditMode(): void {
    this.tryTransition(EditModeState.Viewing, EditModeState.EntryConfirming);
  }

  confirmEntry(): void {
    this.tryTransition(EditModeState.EntryConfirming, EditModeState.Editing);
  }

  cancelEntry(): void {
    this.tryTransition(EditModeState.EntryConfirming, EditModeState.Viewing);
  }

  requestSave(): void {
    this.tryTransition(EditModeState.Editing, EditModeState.SaveConfirming);
  }

  requestDiscard(): void {
    this.tryTransition(EditModeState.Editing, EditModeState.DiscardConfirming);
  }

  requestExit(): void {
    this.tryTransition(EditModeState.Editing, EditModeState.ExitConfirming);
  }

  stayEditing(): void {
    this.tryTransition(EditModeState.ExitConfirming, EditModeState.Editing);
  }

  beginPersist(): void {
    this.tryTransition(
      [EditModeState.Editing, EditModeState.SaveConfirming, EditModeState.ExitConfirming],
      EditModeState.Persisting
    );
  }

  persistSuccess(): void {
    this.tryTransition(EditModeState.Persisting, EditModeState.Viewing);
  }

  persistFailure(): void {
    this.tryTransition(EditModeState.Persisting, EditModeState.Editing);
  }

  beginRevert(): void {
    this.tryTransition(
      [EditModeState.DiscardConfirming, EditModeState.ExitConfirming],
      EditModeState.Reverting
    );
  }

  revertComplete(): void {
    this.tryTransition(EditModeState.Reverting, EditModeState.Viewing);
  }

  hasPulseBeenSeen(): boolean {
    const userId = this.authService.getCurrentUser()?.userId;
    if (!userId) return false;
    return localStorage.getItem(`${EDIT_PULSE_STORAGE_PREFIX}${userId}`) === 'true';
  }

  markPulseSeen(): void {
    const userId = this.authService.getCurrentUser()?.userId;
    if (userId) {
      localStorage.setItem(`${EDIT_PULSE_STORAGE_PREFIX}${userId}`, 'true');
    }
  }

  reset(): void {
    this.state$.next(EditModeState.Viewing);
  }

  private tryTransition(from: EditModeState | EditModeState[], to: EditModeState): void {
    const current = this.state$.value;
    const validFrom = Array.isArray(from) ? from : [from];
    if (validFrom.includes(current)) {
      this.transition(to);
    }
  }

  private transition(next: EditModeState): void {
    this.state$.next(next);
  }
}
